import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { Tag } from 'lucide-react';

const FeaturedProducts = () => {
  const { toast } = useToast();

  const products = [
    {
      image: 'https://images.unsplash.com/photo-1701448149877-228f3163604d',
      name: 'High-Performance Engine Block',
      specs: 'Compatible with SDLG LG936L, LG938L | Cast Iron Construction | OEM Quality',
      price: 'Request Quote',
      code: 'SD-EB-001'
    },
    {
      image: 'https://images.unsplash.com/photo-1643150467600-b41d18de29b0',
      name: 'Hydraulic Pump Assembly',
      specs: 'For SDLG Wheel Loaders | 180 bar Pressure | ISO Certified',
      price: 'Request Quote',
      code: 'SD-HP-180'
    },
    {
      image: 'https://images.unsplash.com/photo-1575651944301-6701ad2635d2',
      name: 'Transmission Gear Set',
      specs: 'Universal Fit | Heat-Treated Steel | Extended Warranty',
      price: 'Request Quote',
      code: 'SD-TG-99X'
    },
    {
      image: 'https://images.unsplash.com/photo-1598358422626-c09b36c321f4',
      name: 'Complete Track Link Assembly',
      specs: 'Heavy-Duty Design | Corrosion Resistant | 2000+ Hours Life',
      price: 'Request Quote',
      code: 'SD-TL-2000'
    },
    {
      image: 'https://horizons-cdn.hostinger.com/44224aab-49f8-4832-a1a7-2f385d4dc6c7/136457bbbd24c1e79df043635e261c4d.jpg',
      name: 'Planetary Gear',
      category: 'Engine Parts',
      specs: 'Code: 41A0002 | Precision Engineered | High Durability',
      price: 'Request Quote',
      code: '41A0002'
    }
  ];

  const handleRequestQuote = (productName, productCode) => {
    toast({
      title: "Quote Request Initiated",
      description: `Inquiry for "${productName}" (Code: ${productCode || 'N/A'}). Our team will contact you shortly.`,
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Featured Products
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Premium quality parts trusted by industry professionals worldwide
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {products.map((product, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="bg-[#2d2d2d] rounded-xl overflow-hidden group transition-all duration-300 hover:shadow-2xl hover:shadow-[#ff6b35]/20 flex flex-col"
            >
              <div className="relative h-64 overflow-hidden bg-white/5">
                <img
                  src={product.image}
                  alt={`${product.name} ${product.code ? `- Code: ${product.code}` : ''} - Trade Zone Equipments`}
                  title={`${product.name} - ${product.code}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/90 via-transparent to-transparent" />
                {product.code && (
                  <div className="absolute top-3 right-3 bg-[#1a1a1a]/80 backdrop-blur-sm px-2 py-1 rounded text-xs font-mono text-[#ff6b35] border border-[#ff6b35]/30">
                    {product.code}
                  </div>
                )}
                {product.category && (
                  <div className="absolute top-3 left-3 bg-[#ff6b35] px-2 py-1 rounded text-xs font-bold text-white shadow-lg">
                    {product.category}
                  </div>
                )}
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#ff6b35] transition-colors duration-300">
                  {product.name}
                </h3>
                
                {product.code && (
                  <div className="flex items-center text-xs text-gray-500 mb-2 font-mono">
                    <Tag size={12} className="mr-1" />
                    PN: {product.code}
                  </div>
                )}

                <p className="text-sm text-gray-400 mb-4 leading-relaxed flex-grow">
                  {product.specs}
                </p>
                <div className="flex items-center justify-between mb-4 pt-4 border-t border-[#4a4a4a]/30">
                  <span className="text-[#ff6b35] font-semibold">{product.price}</span>
                </div>
                <Button
                  onClick={() => handleRequestQuote(product.name, product.code)}
                  className="w-full bg-[#ff6b35] hover:bg-[#ff8c42] text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#ff6b35]/30"
                >
                  Request Quote
                </Button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProducts;